import React, { useState, useMemo } from "react";
import { Row, Col } from "reactstrap";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { Spin } from "antd";
import { Eye, Hash, IndianRupee, Layers, FileText, CalendarDays, Store, Receipt, User, AlertCircle } from "lucide-react";
import {
  GridComponent,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Page,
  Filter,
  Toolbar,
  ExcelExport,
  Sort,
  Resize,
  Group,
  Freeze,
} from "@syncfusion/ej2-react-grids";
import { apiRequest } from "../../../utilities/apiMethod";

export default function GRNGrid({ data = [] }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);

  // Add serial numbers to data
  const enrichedData = useMemo(
    () => data.map((item, idx) => ({ ...item, serialNo: idx + 1 })),
    [data]
  );

  let gridInstance;
  let modalGridInstance;

  const toolbarClick = (args) => {
    if (gridInstance && args.item.id === "GRN_List_GridID_excelexport") {
      gridInstance.excelExport();
    }
  };

  const modalToolbarClick = (args) => {
    if (modalGridInstance && args.item.id === "GRN_Detail_GridID_excelexport") {
      modalGridInstance.excelExport();
    }
  };

  const handleViewDetails = async (row) => {
    try {
      setLoading(true);
      setModalOpen(true);
      setSelectedGRN(row);
      const encodedGrnNo = encodeURIComponent(row.GRNNO);
      const response = await apiRequest(
        "GET",
        `SAPIntegration/getgetGRNDetailsByGrnNo/${encodedGrnNo}`
      );
      if (response.IsSuccess && response.Data) {
        setModalData(response.Data);
      } else {
        setModalData({ error: "No data found" });
      }
    } catch (error) {
      console.error("Error fetching GRN details:", error);
      setModalData({ error: "Failed to fetch details" });
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setModalData(null);
      setSelectedGRN(null);
    }
  };

  // ─── Cell Templates ────────────────────────────────────────────────────────

  const serialNoTemplate = (props) => (
    <div style={{ textAlign: "center", fontWeight: 600, color: "#555" }}>
      {props.serialNo}
    </div>
  );

  const viewDetailsTemplate = (props) => (
    <div style={{ textAlign: "center" }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleViewDetails(props);
        }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#1890ff",
          padding: "4px",
        }}
        type="button"
      >
        <Eye size={20} />
      </button>
    </div>
  );

  const statusTemplate = (props) => {
    const isDraft = props.IsDraft;
    const cfg =
      isDraft === "Draft"
        ? { bg: "#fff7e6", color: "#fa8c16", border: "#ffd591" }
        : { bg: "#f6ffed", color: "#52c41a", border: "#b7eb8f" };
    return (
      <div style={{ textAlign: "center" }}>
        <span
          style={{
            padding: "3px 10px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.3px",
            backgroundColor: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
          }}
        >
          {isDraft}
        </span>
      </div>
    );
  };

  const syncStatusTemplate = (props) => {
    const status = props.SAPSyncStatus;
    const cfg =
      status === "Synced"
        ? { text: "Synced", color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" }
        : status === "Pending"
        ? { text: "Pending", color: "#ff4d4f", bg: "#fff1f0", border: "#ffccc7" }
        : { text: "Unknown", color: "#8c8c8c", bg: "#f5f5f5", border: "#d9d9d9" };
    return (
      <div style={{ textAlign: "center" }}>
        <span
          style={{
            padding: "3px 10px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.3px",
            backgroundColor: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
          }}
        >
          {cfg.text}
        </span>
      </div>
    );
  };

  const amountTemplate = (props) => (
    <div style={{ textAlign: "right", fontWeight: 600, color: "#1a1a2e" }}>
      ₹{Number(props.TotalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
    </div>
  );

  // ─── Modal Helpers ──────────────────────────────────────────────────────────

  const formatVal = (val) =>
    val !== null && val !== undefined && val !== "" ? String(val) : "—";

  const formatCurrency = (val) =>
    val != null
      ? `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
      : "—";

  const InfoCard = ({ icon: Icon, label, value, accent }) => (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e8edf5",
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: accent || "#eef3ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={16} color={accent ? "#fff" : "#4361ee"} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: "#8c9db5", fontWeight: 500, marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, color: "#1a2740", fontWeight: 600, wordBreak: "break-all" }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );

  const StatCard = ({ icon: Icon, label, value, color, bg }) => (
    <div
      style={{
        background: bg,
        border: `1px solid ${color}22`,
        borderRadius: 12,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        flex: 1,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: "#7a8599", fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: color }}>{value}</div>
      </div>
    </div>
  );

  const renderModalContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#8c9db5", fontSize: 14 }}>
            Fetching GRN details…
          </p>
        </div>
      );
    }

    if (!modalData) return null;

    if (modalData.error) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "48px 24px",
            color: "#ff4d4f",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <AlertCircle size={40} color="#ff4d4f" />
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>{modalData.error}</p>
        </div>
      );
    }

    // ── Array response (line items grid with pagination + Excel export) ──────
    if (Array.isArray(modalData) && modalData.length > 0) {
      const keys = Object.keys(modalData[0]);
      const enrichedModalData = modalData.map((row, idx) => ({ _sno: idx + 1, ...row }));
      return (
        <div>
          <p style={{ fontSize: 12, color: "#8c9db5", marginBottom: 12 }}>
            {modalData.length} line item{modalData.length !== 1 ? "s" : ""} found
          </p>
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              border: "1px solid #e8edf5",
              overflow: "hidden",
              boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
            }}
          >
            <GridComponent
              ref={(g) => (modalGridInstance = g)}
              id="GRN_Detail_GridID"
              dataSource={enrichedModalData}
              allowPaging={true}
              allowSorting={true}
              allowFiltering={true}
              filterSettings={{ type: "Excel" }}
              toolbar={["ExcelExport", "Search"]}
              allowExcelExport={true}
              toolbarClick={modalToolbarClick}
              gridLines="Both"
              pageSettings={{ pageSizes: [10, 20, 50, "All"], pageCount: 5 }}
              rowHeight={32}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="_sno"
                  headerText="S No"
                  width="80"
                  textAlign="Center"
                  allowFiltering={false}
                  allowSorting={false}
                  allowExcelExport={false}
                />
                {keys.map((k) => (
                  <ColumnDirective
                    key={k}
                    field={k}
                    headerText={k}
                    width="160"
                    textAlign="Left"
                  />
                ))}
              </ColumnsDirective>
              <Inject services={[Page, Filter, Toolbar, ExcelExport, Sort, Resize]} />
            </GridComponent>
          </div>
        </div>
      );
    }

    // ── Object response: structured layout ─────────────────────────────────
    const d = Array.isArray(modalData) ? {} : modalData;
    const sapStatus = d.SAPSyncStatus || selectedGRN?.SAPSyncStatus;
    const isDraft = d.IsDraft || selectedGRN?.IsDraft;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── Status badges row ─────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 500,
              background: sapStatus === "Synced" ? "#f6ffed" : "#fff1f0",
              color: sapStatus === "Synced" ? "#52c41a" : "#ff4d4f",
              border: `1px solid ${sapStatus === "Synced" ? "#b7eb8f" : "#ffccc7"}`,
            }}
          >
            SAP: {sapStatus || "—"}
          </span>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 500,
              background: isDraft === "Draft" ? "#fff7e6" : "#f6ffed",
              color: isDraft === "Draft" ? "#fa8c16" : "#52c41a",
              border: `1px solid ${isDraft === "Draft" ? "#ffd591" : "#b7eb8f"}`,
            }}
          >
            {isDraft || "—"}
          </span>
        </div>

        {/* ── Summary stats ─────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <StatCard
            icon={Layers}
            label="Total Lines"
            value={formatVal(d.TotalLines ?? selectedGRN?.TotalLines)}
            color="#4361ee"
            bg="#f0f4ff"
          />
          <StatCard
            icon={Package}
            label="Total Qty"
            value={formatVal(d.TotalQty ?? selectedGRN?.TotalQty)}
            color="#7209b7"
            bg="#f8f0ff"
          />
          <StatCard
            icon={IndianRupee}
            label="Total Amount"
            value={formatCurrency(d.TotalAmount ?? selectedGRN?.TotalAmount)}
            color="#0077b6"
            bg="#e8f4fd"
          />
        </div>

        {/* ── Info cards grid ───────────────────────────────────────────── */}
        <div>
          <h6
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#8c9db5",
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              marginBottom: 10,
            }}
          >
            Document Information
          </h6>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            <InfoCard icon={Hash} label="GRN No" value={formatVal(d.GRNNO ?? selectedGRN?.GRNNO)} />
            <InfoCard icon={CalendarDays} label="GRN Date" value={formatVal(d.GRNDATE ?? selectedGRN?.GRNDATE)} />
            <InfoCard icon={FileText} label="IRN No" value={formatVal(d.IRNNo ?? selectedGRN?.IRNNo)} />
            <InfoCard icon={FileText} label="PO No" value={formatVal(d.PONo ?? selectedGRN?.PONo)} />
            <InfoCard icon={Store} label="Store Code" value={formatVal(d.STORECODE ?? selectedGRN?.STORECODE)} />
            <InfoCard icon={Receipt} label="Invoice No" value={formatVal(d.InvoiceNo ?? selectedGRN?.InvoiceNo)} />
            <InfoCard icon={CalendarDays} label="Invoice Date" value={formatVal(d.Invoice_Date ?? selectedGRN?.Invoice_Date)} />
            <InfoCard icon={User} label="Created By" value={formatVal(d.CreateBy ?? selectedGRN?.CreateBy)} />
            <InfoCard icon={CalendarDays} label="Created On" value={formatVal(d.CreatedOn ?? selectedGRN?.CreatedOn)} />
            <InfoCard icon={Hash} label="SAP GRN Ref No" value={formatVal(d.SAPGRNRefNo ?? selectedGRN?.SAPGRNRefNo)} />
            <InfoCard icon={CalendarDays} label="SAP GRN Datetime" value={formatVal(d.SAPGRNDatetime ?? selectedGRN?.SAPGRNDatetime)} />
          </div>
        </div>

        {/* ── SAP Remark ────────────────────────────────────────────────── */}
        {(d.SAPRemark ?? selectedGRN?.SAPRemark) && (
          <div>
            <h6
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#8c9db5",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                marginBottom: 8,
              }}
            >
              SAP Remark
            </h6>
            <div
              style={{
                background: "#fffbe6",
                border: "1px solid #ffe58f",
                borderLeft: "4px solid #faad14",
                borderRadius: 8,
                padding: "12px 14px",
                fontSize: 13,
                color: "#614d00",
                lineHeight: 1.6,
              }}
            >
              <AlertCircle
                size={14}
                color="#faad14"
                style={{ marginRight: 6, verticalAlign: "middle" }}
              />
              {formatVal(d.SAPRemark ?? selectedGRN?.SAPRemark)}
            </div>
          </div>
        )}

        {/* ── Raw key-value fallback for extra fields ───────────────────── */}
        {Object.keys(d).length > 0 && (
          <div>
            <h6
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#8c9db5",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                marginBottom: 10,
              }}
            >
              All Fields
            </h6>
            <div
              style={{
                border: "1px solid #e8edf5",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <tbody>
                  {Object.entries(d).map(([key, value], i) => (
                    <tr key={key} style={{ background: i % 2 === 0 ? "#fff" : "#f9fbff" }}>
                      <td
                        style={{
                          padding: "9px 14px",
                          fontWeight: 600,
                          color: "#3d4f6e",
                          width: "38%",
                          borderBottom: "1px solid #edf1f9",
                          fontSize: 12,
                          verticalAlign: "top",
                        }}
                      >
                        {key}
                      </td>
                      <td
                        style={{
                          padding: "9px 14px",
                          color: "#4a5568",
                          borderBottom: "1px solid #edf1f9",
                          wordBreak: "break-word",
                        }}
                      >
                        {formatVal(value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        className={`grid_compact ${isMobile ? "e-bigger" : ""}`}
        style={{
          position: "relative",
          marginTop: "10px",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          padding: "6px",
          marginBottom: "20px",
        }}
      >
        <Row>
          <Col>
            <GridComponent
              ref={(g) => (gridInstance = g)}
              id="GRN_List_GridID"
              dataSource={enrichedData}
              allowPaging={true}
              allowSorting={true}
              allowFiltering={true}
              filterSettings={{ type: "Excel" }}
              toolbar={["ExcelExport", "Search"]}
              allowExcelExport={true}
              toolbarClick={toolbarClick}
              enableAdaptiveUI={isMobile}
              rowRenderingMode={isMobile ? "Vertical" : "Horizontal"}
              gridLines="Both"
              height={"500px"}
              pageSettings={{
                pageSizes: [10, 20, 30, 40, 50, "All"],
                pageCount: 10,
              }}
              rowHeight={isMobile ? "" : 32}
              frozenColumns={1}
              dataBound={() => {
                if (gridInstance) gridInstance.autoFitColumns();
              }}
            >
              <ColumnsDirective>
                {/* S.No — uses template so it always shows a number */}
                <ColumnDirective
                  field="serialNo"
                  headerText="S No"
                  width="80"
                  textAlign="Center"
                  isPrimaryKey={true}
                  allowFiltering={false}
                  allowSorting={false}
                  template={serialNoTemplate}
                />

                <ColumnDirective field="GRNNO" headerText="GRN No" width="200" textAlign="Left" />
                <ColumnDirective field="GRNDATE" headerText="GRN Date" width="180" textAlign="Center" type="date" format="dd/MM/yyyy" />
                <ColumnDirective field="IRNNo" headerText="IRN No" width="200" textAlign="Left" />
                <ColumnDirective field="PONo" headerText="PO No" width="180" textAlign="Left" />
                <ColumnDirective field="STORECODE" headerText="Store Code" width="150" textAlign="Left" />
                <ColumnDirective field="InvoiceNo" headerText="Invoice No" width="180" textAlign="Left" />
                <ColumnDirective field="Invoice_Date" headerText="Invoice Date" width="180" textAlign="Center" type="date" format="dd/MM/yyyy" />

                {/* ── 3 previously missing columns ── */}
                <ColumnDirective field="TotalLines" headerText="Total Lines" width="170" textAlign="Center" />
                <ColumnDirective field="TotalQty" headerText="Total Qty" width="170" textAlign="Center" />
                <ColumnDirective
                  field="TotalAmount"
                  headerText="Total Amount"
                  width="170"
                  textAlign="Right"
                  template={amountTemplate}
                />

                <ColumnDirective field="SAPSyncStatus" headerText="SAP Sync Status" width="180" textAlign="Center" template={syncStatusTemplate} />
                <ColumnDirective field="SAPGRNRefNo" headerText="SAP GRN Ref No" width="200" textAlign="Left" />
                <ColumnDirective field="SAPGRNDatetime" headerText="SAP GRN Datetime" width="220" textAlign="Center" type="datetime" format="dd/MM/yyyy HH:mm" />
                <ColumnDirective field="SAPRemark" headerText="SAP Remark" width="250" textAlign="Left" />
                <ColumnDirective field="IsDraft" headerText="Status" width="150" textAlign="Center" template={statusTemplate} />
                <ColumnDirective field="CreateBy" headerText="Created By" width="150" textAlign="Left" />
                <ColumnDirective field="CreatedOn" headerText="Created On" width="220" textAlign="Center" type="datetime" format="dd/MM/yyyy HH:mm" />

                <ColumnDirective
                  headerText="View Details"
                  width="130"
                  textAlign="Center"
                  template={viewDetailsTemplate}
                  allowFiltering={false}
                  allowSorting={false}
                  allowExcelExport={false}
                  freeze="Right"
                />
              </ColumnsDirective>
              <Inject services={[Page, Filter, Toolbar, ExcelExport, Sort, Group, Resize, Freeze]} />
            </GridComponent>
          </Col>
        </Row>
      </div>

      {/* ── Modal ─────────────────────────────────────────────────────────────── */}
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        size="xl"
        centered
        scrollable
        style={{ maxWidth: "92vw", width: "92vw" }}
      >
        <ModalHeader toggle={toggleModal}>
          GRN Details{selectedGRN ? ` — ${selectedGRN.GRNNO}` : ""}
        </ModalHeader>

        <ModalBody
          style={{
            background: "#f8f9fb",
            padding: "24px",
            maxHeight: "75vh",
            overflowY: "auto",
          }}
        >
          {renderModalContent()}
        </ModalBody>
      </Modal>
    </>
  );
}